/**
 * Policy Versioning UI primitives.
 *
 * Scope: used only by PolicyDetail. Provides:
 *   • VersionSelectorButton  — the "Version N ▾" trigger + popover list.
 *   • UploadPdfVersionDialog — drag-and-drop PDF upload modal.
 *   • AIRegenerateVersionDialog — AI regen modal with optional instruction.
 *
 * All API calls live here (thin axios) so PolicyDetail only needs to
 * refresh after each action.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  ChevronDown, CheckCircle2, FileText, Sparkles, Upload, X, Loader2, Paperclip, FileType,
} from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${t}` } };
};

// --- Small helpers ---------------------------------------------------------
export const SOURCE_LABEL = {
  original: 'Original',
  pdf_upload: 'Uploaded PDF',
  ai_generated: 'AI Generated',
};

const SOURCE_TONE = {
  original: 'bg-slate-100 text-slate-700 border-slate-200',
  pdf_upload: 'bg-blue-50 text-blue-700 border-blue-200',
  ai_generated: 'bg-violet-50 text-violet-700 border-violet-200',
};

/** Small pill badge that identifies the origin of a version. */
export const SourceBadge = ({ sourceType, className = '' }) => {
  const label = SOURCE_LABEL[sourceType] || 'Original';
  const tone = SOURCE_TONE[sourceType] || SOURCE_TONE.original;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide rounded-full border px-1.5 py-0.5 ${tone} ${className}`}
      data-testid={`version-source-badge-${sourceType || 'original'}`}
    >
      {label}
    </span>
  );
};

// --- Version selector popover ---------------------------------------------
/**
 * Clickable "Version N ▾" chip that opens a compact timeline of every
 * version. Selecting a version calls `onSelect(versionNumber)` — the
 * parent then loads that version's snapshot into the preview WITHOUT
 * changing which version is active.
 */
export const VersionSelectorButton = ({
  versions,
  activeVersionNumber,
  previewVersionNumber,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => (versions || [])
      .filter((v) => Number(v?.version_number) >= 1)  // never render v0 / legacy garbage
      .slice()
      .sort((a, b) => (b.version_number || 0) - (a.version_number || 0)),
    [versions],
  );
  const currentLabel = previewVersionNumber || activeVersionNumber || 1;

  const pick = (n) => {
    setOpen(false);
    if (onSelect) onSelect(n);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full border px-2.5 py-1 transition-colors border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          data-testid="policy-version-selector"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>Version {currentLabel}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" data-testid="policy-version-popover">
        <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Version history
        </div>
        <div
          className="max-h-80 overflow-y-auto overscroll-contain scroll-smooth space-y-1 pr-1"
          data-testid="policy-version-history-scroll"
          // Popover roots occasionally swallow wheel events on hover.
          // Explicitly stopping propagation guarantees the wheel is
          // routed to *this* scroll container — matching the standard
          // OS/browser expectation the user described.
          onWheel={(e) => e.stopPropagation()}
        >
          {sorted.map((v) => {
            const isActive = v.is_active;
            const isPreview = v.version_number === previewVersionNumber;
            const filename = v.source_filename ? ` · ${v.source_filename}` : '';
            const src = SOURCE_LABEL[v.source_type] || 'Original';
            return (
              <button
                key={v.id || v.version_number}
                type="button"
                onClick={() => pick(v.version_number)}
                className={`w-full text-left rounded-lg border p-2.5 hover:border-blue-300 hover:bg-blue-50 transition-colors ${isPreview ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}
                data-testid={`policy-version-row-${v.version_number}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-800">v{v.version_number}</span>
                  <div className="flex items-center gap-1.5">
                    <SourceBadge sourceType={v.source_type} />
                    {isActive && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-wide" data-testid={`policy-version-active-${v.version_number}`}>
                        Active
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-600 line-clamp-1">
                  {src}{filename}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {v.created_at ? formatDate(v.created_at) : ''}
                  {v.created_by_name ? ` · ${v.created_by_name}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// --- Upload PDF dialog -----------------------------------------------------
/**
 * Drag-and-drop PDF upload modal. Rejects non-PDF files client-side and
 * surfaces server-side extraction errors via toast.
 */
export const UploadPdfVersionDialog = ({ open, onOpenChange, policyId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const clear = () => { setFile(null); setUploading(false); setDragOver(false); };
  const close = () => { if (!uploading) { onOpenChange(false); clear(); } };

  const takeFile = useCallback((f) => {
    if (!f) return;
    const okType = /pdf$/i.test(f.type || '') || /\.pdf$/i.test(f.name || '');
    if (!okType) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('PDF exceeds the 20 MB limit.');
      return;
    }
    setFile(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    takeFile(e.dataTransfer?.files?.[0]);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const submit = async () => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `/api/policies/${policyId}/versions/upload-pdf`,
        fd,
        { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Created v${r.data.version_number} from ${file.name}. Existing versions are unchanged.`);
      onOpenChange(false);
      clear();
      if (onSuccess) onSuccess(r.data.version_number);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(String(detail));
    } finally {
      setUploading(false);
    }
  };

  const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="policy-upload-pdf-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-blue-600" /> Upload your own PDF
          </DialogTitle>
          <DialogDescription>
            Create a new policy version using your PDF. Your existing versions will not change.
          </DialogDescription>
        </DialogHeader>
        {!file ? (
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`mt-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50'}`}
            data-testid="policy-upload-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-700">Drag and drop your PDF here</p>
            <p className="text-xs text-slate-500">or</p>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => takeFile(e.target.files?.[0])}
              className="hidden"
              data-testid="policy-upload-input"
            />
            <Button
              type="button" variant="outline" size="sm"
              className="mt-2"
              onClick={() => inputRef.current?.click()}
              data-testid="policy-upload-browse-btn"
            >
              Browse files
            </Button>
            <p className="mt-3 text-[11px] text-slate-400">PDF only · up to 20 MB</p>
          </div>
        ) : (
          <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid="policy-upload-selected">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <FileType className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{fmtSize(file.size)}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFile(null)} disabled={uploading} data-testid="policy-upload-remove-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={close} disabled={uploading} data-testid="policy-upload-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!file || uploading} data-testid="policy-upload-confirm-btn">
            {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>) : (<><Upload className="w-4 h-4 mr-2" /> Create new version</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- AI regeneration dialog ------------------------------------------------
/**
 * Ask the LLM to refresh the currently-viewed version and produce a new
 * draft version. The source version is not mutated.
 */
export const AIRegenerateVersionDialog = ({ open, onOpenChange, policyId, sourceVersionNumber, sourceVersionSummary, onSuccess }) => {
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) { onOpenChange(false); setInstruction(''); } };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `/api/policies/${policyId}/versions/ai-regenerate`,
        { source_version_number: sourceVersionNumber, instruction: instruction || null },
        authHeaders(),
      );
      toast.success(`Created v${r.data.version_number} using AI. Prior versions are unchanged.`);
      onOpenChange(false);
      setInstruction('');
      if (onSuccess) onSuccess(r.data.version_number);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'AI generation failed. Please try again.';
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="policy-ai-regenerate-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" /> Regenerate with AI
          </DialogTitle>
          <DialogDescription>
            AI will create a new draft version. The source version will not be changed.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-2" data-testid="policy-ai-source-info">
            <FileText className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="text-sm text-slate-700">
              Source: <span className="font-semibold">Version {sourceVersionNumber}</span>
              {sourceVersionSummary?.source_type && (
                <>{' '}<SourceBadge sourceType={sourceVersionSummary.source_type} className="align-middle ml-1" /></>
              )}
            </span>
          </div>
          <div>
            <Label htmlFor="ai-inst" className="text-xs font-semibold text-slate-600">Optional instruction</Label>
            <Textarea
              id="ai-inst"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Example: Make this policy shorter and more suitable for a 50-person SaaS company."
              className="mt-1"
              rows={4}
              data-testid="policy-ai-instruction"
              disabled={busy}
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={close} disabled={busy} data-testid="policy-ai-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="policy-ai-confirm-btn">
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate new version</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Preview banner --------------------------------------------------------
/** Slim banner shown while the user is previewing a non-active version. */
export const HistoricalVersionBanner = ({
  previewVersionNumber,
  onActivate,
  onExitPreview,
  activating,
  disabled,
  disabledReason,
}) => (
  <div
    className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
    data-testid="policy-preview-banner"
  >
    <FileText className="w-4 h-4 text-amber-700 shrink-0" />
    <div className="text-sm text-amber-900 flex-1">
      Previewing <span className="font-semibold">Version {previewVersionNumber}</span>. This version is not active. Set it as the active version to roll back and make it the default going forward.
      {disabled && disabledReason && (
        <div className="text-xs text-amber-800/80 mt-0.5">{disabledReason}</div>
      )}
    </div>
    <Button
      size="sm" variant="outline"
      onClick={onExitPreview}
      className="border-amber-300 text-amber-800 hover:bg-amber-100"
      data-testid="policy-preview-exit-btn"
    >
      Exit preview
    </Button>
    <Button
      size="sm"
      onClick={onActivate}
      disabled={disabled || activating}
      className="bg-amber-600 hover:bg-amber-700 text-white"
      data-testid="policy-set-active-btn"
    >
      {activating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating…</>) : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Set as active version</>)}
    </Button>
  </div>
);
